import { useMemo } from 'react';
import type { IconElement } from '@backstage/frontend-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { policyEntityCreatePermission } from '@backstage-community/plugin-rbac-common';

import type { SidebarSlots } from '../components/sidebar/sidebar';
import { findGlyph } from '../find-glyph';
import {
  type NavConfig,
  type PageNavItems,
  resolveNavSlots,
} from '../nav-config';

/**
 * Places AppNav's pages in the sidebar slots, as the extension config says.
 *
 * @remarks
 * One thing stays in code on purpose: the RBAC page is placed only with the
 * RBAC admin permission, whatever the config says.
 *
 * @param config - The parsed `nav-content:app` config.
 * @param navItems - AppNav's pages. The sidebar takes the ones the config places.
 * @returns The items per slot.
 *
 * @public
 */
export function useNavSlots(
  config: NavConfig,
  navItems: PageNavItems<IconElement>,
): SidebarSlots {
  // Gate on the create permission: a BasicPermission usePermission can
  // evaluate, unlike the read permission (a ResourcePermission that DENYs
  // without a resourceRef).
  const { allowed: canAdministerRbac } = usePermission({
    permission: policyEntityCreatePermission,
  });

  return useMemo(
    () =>
      resolveNavSlots(config, navItems, {
        canAdministerRbac,
        findGlyph,
      }),
    [config, navItems, canAdministerRbac],
  );
}
